/**
 * PendingApprovalsTab Component
 * Shows checks waiting for QC approval with search, filters, bulk select and pagination
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle, Eye, X, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Check type display names
const CHECK_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Check',
  'court_advocate_check': 'Court Advocate Check',
  'criminal_advocate_check': 'Criminal Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'global_database': 'Global Database Check',
  'world_criminal_db': 'World Criminal Database',
  'india_criminal_db': 'India Criminal Database'
};

const getCheckDisplayName = (checkType) => CHECK_NAMES[checkType] || checkType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';

export default function PendingApprovalsTab({ 
  checkWisePending, 
  onRefresh, 
  onShowCaseDetail 
}) {
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    checkType: 'all',
    client: 'all',
    entity: 'all',
    dateFilter: 'all',
    sortBy: 'recent'
  });
  const [bulkApproving, setBulkApproving] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const pendingApprovalChecks = checkWisePending?.pending_approval_checks || [];
  
  // Get unique values for filters
  const uniqueCheckTypes = [...new Set(pendingApprovalChecks.map(c => c.check_type))];
  const uniqueClients = [...new Set(pendingApprovalChecks.map(c => c.client_name).filter(Boolean))];
  const uniqueEntities = [...new Set(pendingApprovalChecks.map(c => c.entity_name).filter(Boolean))];
  
  // Apply search and filters
  let filteredChecks = [...pendingApprovalChecks];
  
  // Search
  if (searchTerm) {
    const search = searchTerm.toLowerCase();
    filteredChecks = filteredChecks.filter(c => 
      c.case_id?.toLowerCase().includes(search) ||
      c.employee_name?.toLowerCase().includes(search) ||
      c.client_name?.toLowerCase().includes(search) ||
      c.entity_name?.toLowerCase().includes(search)
    );
  }
  
  // Filter by check type
  if (filters.checkType !== 'all') {
    filteredChecks = filteredChecks.filter(c => c.check_type === filters.checkType);
  }
  
  // Filter by client
  if (filters.client !== 'all') {
    filteredChecks = filteredChecks.filter(c => c.client_name === filters.client);
  }
  
  // Filter by entity
  if (filters.entity !== 'all') {
    filteredChecks = filteredChecks.filter(c => c.entity_name === filters.entity);
  }
  
  // Filter by date
  if (filters.dateFilter !== 'all') {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    filteredChecks = filteredChecks.filter(c => {
      if (!c.created_at) return false;
      const checkDate = new Date(c.created_at);
      switch (filters.dateFilter) {
        case 'today':
          return checkDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return checkDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return checkDate >= monthAgo;
        default:
          return true;
      }
    });
  }
  
  // Sort
  if (filters.sortBy === 'recent') {
    filteredChecks.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  } else if (filters.sortBy === 'oldest') {
    filteredChecks.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  }
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredChecks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedChecks = filteredChecks.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);
  
  const allSelected = paginatedChecks.length > 0 && selected.length === paginatedChecks.length;
  
  const handleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(paginatedChecks.map(c => ({ case_id: c.case_id, check_id: c.check_id })));
    }
  };
  
  const handleCheckSelect = (check) => {
    const key = `${check.case_id}-${check.check_id}`;
    const isSelected = selected.some(s => `${s.case_id}-${s.check_id}` === key);
    if (isSelected) {
      setSelected(selected.filter(s => `${s.case_id}-${s.check_id}` !== key));
    } else {
      setSelected([...selected, { case_id: check.case_id, check_id: check.check_id }]);
    }
  };
  
  const handleBulkApprove = async (autoAssign = false) => {
    if (selected.length === 0) {
      toast.error('Please select at least one check to approve');
      return;
    }
    
    setBulkApproving(true);
    try {
      const response = await axios.post('/quick-approve/bulk-approve-assign', {
        check_ids: selected,
        auto_assign: autoAssign
      });
      toast.success(response.data.message || `${selected.length} checks approved successfully`);
      setSelected([]);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve checks');
    } finally {
      setBulkApproving(false);
    }
  };
  
  const resetFilters = () => {
    setSearchTerm('');
    setFilters({ checkType: 'all', client: 'all', entity: 'all', dateFilter: 'all', sortBy: 'recent' });
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Pending Approvals</h1>
          <p className="text-slate-600">Checks waiting for QC approval before assignment</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Badge className="bg-orange-100 text-orange-700 text-lg px-4 py-2">
            {filteredChecks.length} of {pendingApprovalChecks.length} Checks
          </Badge>
        </div>
      </div>

      {/* Search and Filters Row */}
      <Card className="border-slate-200 shadow-sm mb-4">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search by Case ID, Employee, Client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Client Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Client:</label>
              <Select value={filters.client} onValueChange={(v) => setFilters({...filters, client: v})}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {uniqueClients.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Entity Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Entity:</label>
              <Select value={filters.entity} onValueChange={(v) => setFilters({...filters, entity: v})}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {uniqueEntities.map(e => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Received:</label>
              <Select value={filters.dateFilter} onValueChange={(v) => setFilters({...filters, dateFilter: v})}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sort */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Sort:</label>
              <Select value={filters.sortBy} onValueChange={(v) => setFilters({...filters, sortBy: v})}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Latest First" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Latest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={resetFilters}
              className="text-slate-500"
            >
              <X className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selected.length > 0 && (
        <Card className="border-emerald-300 bg-emerald-50 shadow-sm mb-4">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-emerald-800">{selected.length} check(s) selected</span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setSelected([])}
                  className="border-slate-300"
                >
                  Clear Selection
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => handleBulkApprove(false)}
                  disabled={bulkApproving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="bulk-approve-btn"
                >
                  {bulkApproving ? 'Approving...' : 'Approve Selected'}
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => handleBulkApprove(true)}
                  disabled={bulkApproving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="bulk-approve-assign-btn"
                >
                  {bulkApproving ? 'Processing...' : 'Approve & Auto-Assign'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm">
        {/* Top pagination bar (May 24, 2026) — duplicates the bottom
            controls so QC team doesn't have to scroll past 20 rows to
            change page. Bound to the same state, so the two stay in sync. */}
        {filteredChecks.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50" data-testid="pending-approvals-pagination-top">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredChecks.length)} of {filteredChecks.length} checks
              </span>
              <Select value={itemsPerPage.toString()} onValueChange={(val) => { setItemsPerPage(parseInt(val)); setCurrentPage(1); }}>
                <SelectTrigger className="w-[100px] h-8" data-testid="pending-approvals-page-size-top">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} data-testid="pending-approvals-first-top">
                First
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="pending-approvals-prev-top">
                Previous
              </Button>
              <span className="px-3 py-1 text-sm font-medium">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} data-testid="pending-approvals-next-top">
                Next
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} data-testid="pending-approvals-last-top">
                Last
              </Button>
            </div>
          </div>
        )}
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input 
                    type="checkbox" 
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Case ID</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Client / Entity</TableHead>
                <TableHead>Check Type</TableHead>
                <TableHead>Received</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedChecks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                    <p>No checks pending approval</p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedChecks.map((check, idx) => {
                  const isSelected = selected.some(s => `${s.case_id}-${s.check_id}` === `${check.case_id}-${check.check_id}`);
                  return (
                    <TableRow 
                      key={`${check.case_id}-${check.check_id}-${idx}`} 
                      className={`hover:bg-slate-50 ${isSelected ? 'bg-emerald-50' : ''}`}
                    >
                      <TableCell>
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => handleCheckSelect(check)}
                          className="w-4 h-4 rounded border-slate-300"
                          data-testid={`select-check-${check.check_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium">{check.case_id || 'N/A'}</TableCell>
                      <TableCell>
                        <p className="font-medium">{check.employee_name || 'N/A'}</p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{check.client_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{check.entity_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {getCheckDisplayName(check.check_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {check.created_at ? new Date(check.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onShowCaseDetail?.(check.case_id)}
                          data-testid={`review-check-${check.check_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination Controls */}
        {filteredChecks.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredChecks.length)} of {filteredChecks.length} checks
              </span>
              <Select value={itemsPerPage.toString()} onValueChange={(val) => { setItemsPerPage(parseInt(val)); setCurrentPage(1); }}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                First
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                Previous
              </Button>
              <span className="px-3 py-1 text-sm font-medium">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
                Next
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
